import React, { useState, useEffect, useRef } from 'react';
import { api } from './lib/api';
import ClientAutocomplete from './components/ClientAutocomplete';
import FournisseurAutocomplete from './components/FournisseurAutocomplete';
import OperationEntry from './OperationEntry';
import { useCompany } from './contexts/CompanyContext';
import { searchPcg, type PcgAccount } from './lib/pcg2026';
import { useTvaRates } from './hooks/useTvaRates';

interface TransactionEntryProps {
  entrepriseId: string;
  exerciceId: string;
  onSuccess?: () => void;
}


const TransactionEntry: React.FC<TransactionEntryProps> = ({
  entrepriseId,
  exerciceId,
  onSuccess,
}) => {
  const { entreprises } = useCompany();
  const { rates: tvaRates, pcgMap } = useTvaRates();

  const applyTvaForPcg = (numero: string) => {
    const entry = Object.entries(pcgMap).find(([, comptes]) =>
      comptes.split(',').map(c => c.trim()).some(c => c && (numero.startsWith(c) || c.startsWith(numero)))
    );
    if (entry) handleTvaChange(Number(entry[0]));
  };
  const exercice = entreprises
    .find((e) => e.id === entrepriseId)
    ?.exercices?.find((ex: any) => ex.id === exerciceId) as any;
  const exerciceDebut = exercice?.debut ? new Date(exercice.debut).toISOString().slice(0, 10) : null;
  const exerciceFin = exercice?.fin ? new Date(exercice.fin).toISOString().slice(0, 10) : null;

  const [mode, setMode] = useState<'banque' | 'operation'>('banque');
  const [date, setDate] = useState(() => localStorage.getItem('saisie_last_date') || '');
  const [libelle, setLibelle] = useState('');
  const [ttcInput, setTtcInput] = useState('');
  const [htInput, setHtInput] = useState('');
  // Numeric values parsed from inputs (normalized . as decimal)
  const parseAmount = (s: string): number | '' => {
    const normalized = s.replace(',', '.');
    const n = parseFloat(normalized);
    return isNaN(n) ? '' : n;
  };
  const ttcCentimes = parseAmount(ttcInput);
  const [type, setType] = useState<'recette' | 'depense'>('depense');
  const [tauxTva, setTauxTva] = useState<number>(20);
  const [comptePrincipal, setComptePrincipal] = useState('');
  const [compteSearch, setCompteSearch] = useState('');
  const [compteOptions, setCompteOptions] = useState<PcgAccount[]>([]);
  const [pieceRef, setPieceRef] = useState('');
  const [justificatifFile, setJustificatifFile] = useState<File | null>(null);
  const [justificatifId, setJustificatifId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [fournisseurId, setFournisseurId] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<any>(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (compteSearch.length < 2) {
      setCompteOptions([]);
      return;
    }
    setCompteOptions(searchPcg(compteSearch).slice(0, 30));
  }, [compteSearch]);

  const handleTtcChange = (raw: string) => {
    setTtcInput(raw);
    const val = parseAmount(raw);
    if (val === '' || val === 0) { setHtInput(''); return; }
    const ht = Math.round((val / (1 + tauxTva / 100)) * 100) / 100;
    setHtInput(String(ht));
  };

  const handleHtChange = (raw: string) => {
    setHtInput(raw);
    const val = parseAmount(raw);
    if (val === '' || val === 0) { setTtcInput(''); return; }
    const ttc = Math.round(val * (1 + tauxTva / 100) * 100) / 100;
    setTtcInput(String(ttc));
  };

  const handleTvaChange = (taux: number) => {
    setTauxTva(taux);
    const ttc = parseAmount(ttcInput);
    if (ttc !== '') {
      const ht = Math.round((ttc / (1 + taux / 100)) * 100) / 100;
      setHtInput(String(ht));
    }
  };

  const resetForm = () => {
    setLibelle('');
    setTtcInput('');
    setHtInput('');
    setComptePrincipal('');
    setCompteSearch('');
    setPieceRef('');
    setJustificatifFile(null);
    setJustificatifId(null);
    setDate(new Date().toISOString().slice(0, 10));
    setOcrData(null);
    setOcrError(null);
    setClientId(null);
    setFournisseurId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowSuccess(false);
    setSubmitting(true);

    try {
      if (!date) {
        throw new Error('La date est obligatoire');
      }
      // Validate date is within exercice period
      if (exerciceDebut && date < exerciceDebut) {
        throw new Error(`La date doit être après le début de l'exercice (${exerciceDebut})`);
      }
      if (exerciceFin && date > exerciceFin) {
        throw new Error(`La date doit être avant la fin de l'exercice (${exerciceFin})`);
      }
      if (!ttcCentimes || ttcCentimes <= 0) {
        throw new Error('Montant TTC doit être positif');
      }
      if (!comptePrincipal) {
        throw new Error('Compte PCG requis');
      }
      if (!libelle.trim()) {
        throw new Error('Libellé requis');
      }

      let finalJustificatifId = justificatifId;
      if (justificatifFile && !justificatifId) {
        setUploading(true);
        const uploadData = await api.justificatifs.upload(justificatifFile, entrepriseId);
        finalJustificatifId = uploadData.id;
        setJustificatifId(uploadData.id);
        setUploading(false);
      }

      const payload = {
        entreprise_id: entrepriseId,
        exercice_id: exerciceId,
        date: new Date(date).toISOString(),
        libelle: libelle.trim(),
        type,
        ttc_centimes: Math.round(Number(ttcCentimes) * 100),
        taux_tva: tauxTva,
        compte_principal: comptePrincipal,
        piece_ref: pieceRef.trim() || undefined,
        justificatif_id: finalJustificatifId ?? undefined,
        justificatif_ids: finalJustificatifId ? [finalJustificatifId] : undefined,
        client_id: clientId ?? undefined,
        fournisseur_id: fournisseurId ?? undefined,
      };

      await api.transactions.create(payload);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
      }, 1500);
      if (onSuccess) onSuccess();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleParseOcr = async () => {
    if (!justificatifId) return;
    setOcrLoading(true);
    setOcrError(null);
    try {
      const data = await api.justificatifs.parse(justificatifId, entrepriseId);
      setOcrData(data);
    } catch (e: any) {
      setOcrError(e.message);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleApplyOcr = () => {
    if (!ocrData?.parsed) return;
    const p = ocrData.parsed;
    if (p.vendor) setLibelle(p.vendor);
    if (p.date) {
      setDate(p.date);
    }
    if (p.amount_centimes !== undefined) {
      handleTtcChange(String(p.amount_centimes / 100));
    }
  };

  const inputClass =
    'w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';
  const labelClass =
    'text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1';

  return (
    <div className="max-w-lg mx-auto">
      {/* Page header */}
      <p className="text-[10px] uppercase text-amber-500 tracking-widest font-semibold">
        Comptabilité
      </p>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Écriture</h2>

      {/* Mode tab switcher */}
      <div className="flex border-b border-gray-100 mb-6" role="tablist">
        <button
          role="tab"
          aria-selected={mode === 'banque'}
          onClick={() => setMode('banque')}
          className={`flex-1 py-3 text-sm text-center transition-colors ${
            mode === 'banque'
              ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500 font-semibold'
              : 'text-gray-400 hover:text-gray-600 bg-white'
          }`}
        >
          BANQUE
        </button>
        <button
          role="tab"
          aria-selected={mode === 'operation'}
          onClick={() => setMode('operation')}
          className={`flex-1 py-3 text-sm text-center transition-colors ${
            mode === 'operation'
              ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500 font-semibold'
              : 'text-gray-400 hover:text-gray-600 bg-white'
          }`}
        >
          OPÉRATION
        </button>
      </div>

      {mode === 'banque' && (
        <>
          {/* Sub-tab switcher for Banque */}
          <div className="flex border-b border-gray-100 mb-6" role="tablist">
            <button
              role="tab"
              aria-selected={type === 'recette'}
              onClick={() => setType('recette')}
              className={`flex-1 py-3 text-sm text-center transition-colors ${
                type === 'recette'
                  ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500 font-semibold'
                  : 'text-gray-400 hover:text-gray-600 bg-white'
              }`}
            >
              RECETTE
            </button>
            <button
              role="tab"
              aria-selected={type === 'depense'}
              onClick={() => setType('depense')}
              className={`flex-1 py-3 text-sm text-center transition-colors ${
                type === 'depense'
                  ? 'bg-red-50 text-red-700 border-b-2 border-red-500 font-semibold'
                  : 'text-gray-400 hover:text-gray-600 bg-white'
              }`}
            >
              DÉPENSE
            </button>
          </div>
        </>
      )}

      {/* Success feedback */}
      {showSuccess && (
        <div className="flex justify-center py-8">
          <span className="text-4xl text-green-500 animate-bounce">✓</span>
        </div>
      )}

      {!showSuccess && mode === 'banque' && (
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
            {/* DATE */}
            <div>
              <label htmlFor="saisie-date">
                <p className={labelClass}>Date</p>
              </label>
              <input
                id="saisie-date"
                type="date"
                value={date}
                min={exerciceDebut || undefined}
                max={exerciceFin || undefined}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (e.target.value) localStorage.setItem('saisie_last_date', e.target.value);
                }}
                required
                className={inputClass}
              />
            </div>

            {/* LIBELLÉ */}
            <div>
              <p className={labelClass}>Libellé</p>
              <input
                type="text"
                value={libelle}
                onChange={(e) => {
                  setLibelle(e.target.value);
                }}
                placeholder="Ex: Achat fournitures bureau"
                required
                className={inputClass}
              />
            </div>

            {/* COMPTE PCG */}
            <div className="relative">
              <p className={labelClass}>Compte PCG</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  🔍
                </span>
                <input
                  type="text"
                  value={compteSearch}
                  onChange={(e) => setCompteSearch(e.target.value)}
                  placeholder="Rechercher un compte (ex: 6064, 512)"
                  className={`${inputClass} pl-8`}
                />
              </div>
              {compteOptions.length > 0 && (
                <ul
                  role="listbox"
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                >
                  {compteOptions.map((acc) => (
                    <li
                      key={acc.numero}
                      role="option"
                      aria-selected={comptePrincipal === acc.numero}
                      onClick={() => {
                        setComptePrincipal(acc.numero);
                        setCompteSearch(`${acc.numero} — ${acc.libelle}`);
                        setCompteOptions([]);
                        applyTvaForPcg(acc.numero);
                      }}
                      className="px-4 py-2 text-sm hover:bg-amber-50 cursor-pointer border-b border-gray-50 last:border-0"
                    >
                      <strong>{acc.numero}</strong> — {acc.libelle}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* TTC | HT | TVA — 4/4/2 cols */}
            <div className="grid grid-cols-10 gap-3">
              <div className="col-span-4">
                <p className={labelClass}>TTC</p>
                <input
                  type="text"
                  inputMode="decimal"
                  value={ttcInput}
                  onChange={(e) => handleTtcChange(e.target.value)}
                  placeholder="0.00"
                  required
                  className={inputClass}
                />
              </div>
              <div className="col-span-4">
                <p className={labelClass}>HT</p>
                <input
                  type="text"
                  inputMode="decimal"
                  value={htInput}
                  onChange={(e) => handleHtChange(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
              <div className="col-span-2">
                <p className={labelClass}>TVA</p>
                <select
                  value={tauxTva}
                  onChange={(e) => handleTvaChange(Number(e.target.value))}
                  className={inputClass}
                >
                  {tvaRates.map((r) => (
                    <option key={r} value={r}>{r}%</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CLIENT (recette only) */}
            {type === 'recette' && (
              <div>
                <p className={labelClass}>Client <span className="text-gray-300">(optionnel)</span></p>
                <ClientAutocomplete
                  entrepriseId={entrepriseId}
                  value={clientId}
                  onChange={setClientId}
                />
              </div>
            )}

            {/* FOURNISSEUR (depense only) */}
            {type === 'depense' && (
              <div>
                <p className={labelClass}>Fournisseur <span className="text-gray-300">(optionnel)</span></p>
                <FournisseurAutocomplete
                  entrepriseId={entrepriseId}
                  value={fournisseurId}
                  onChange={setFournisseurId}
                />
              </div>
            )}

            {/* RÉF. PIÈCE */}
            <div>
              <p className={labelClass}>
                Réf. pièce <span className="text-gray-300">(optionnel)</span>
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  #
                </span>
                <input
                  type="text"
                  value={pieceRef}
                  onChange={(e) => setPieceRef(e.target.value)}
                  placeholder="FAC-2025-001"
                  className={`${inputClass} pl-8`}
                />
              </div>
            </div>

            {/* JUSTIFICATIF */}
            <div>
              <p className={labelClass}>
                Justificatif{' '}
                <span className="text-gray-300">(optionnel)</span>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setJustificatifFile(file);
                  setJustificatifId(null);
                }}
              />
              {!justificatifFile ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-amber-300 hover:bg-amber-50 transition-colors"
                >
                  <p className="text-2xl mb-1">📎</p>
                  <p className="text-sm text-gray-600 font-medium">
                    Joindre un fichier
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, PNG, JPG jusqu'à 10 Mo
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
                  <span className="text-green-500">✓</span>
                  <span className="truncate">{justificatifFile.name}</span>
                  {uploading && (
                    <span className="text-gray-400 text-xs ml-auto">
                      Téléchargement...
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* OCR */}
            {justificatifId && !ocrData && !ocrLoading && (
              <button
                type="button"
                onClick={handleParseOcr}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors"
              >
                Analyser OCR
              </button>
            )}
            {ocrLoading && (
              <span className="text-sm text-gray-500">
                Analyse OCR en cours...
              </span>
            )}
            {ocrError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                {ocrError}
              </div>
            )}
            {ocrData && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Résultat OCR :
                </p>
                <pre className="whitespace-pre-wrap text-xs text-gray-600 max-h-48 overflow-auto bg-white p-3 rounded border border-gray-100">
                  {ocrData.text}
                </pre>
                <button
                  type="button"
                  onClick={handleApplyOcr}
                  className="mt-3 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600 transition-colors"
                >
                  Appliquer les champs
                </button>
              </div>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={submitting || uploading}
            className={`w-full text-white rounded-xl py-4 font-semibold text-sm disabled:opacity-40 transition-colors mt-2 ${
              type === 'recette'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {submitting || uploading ? 'Enregistrement...' : 'Enregistrer »'}
          </button>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mt-2">
              {error}
            </div>
          )}
        </form>
      )}

      {mode === 'operation' && (
        <OperationEntry
          entrepriseId={entrepriseId}
          exerciceId={exerciceId}
          exerciceDebut={exerciceDebut || undefined}
          exerciceFin={exerciceFin || undefined}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};

export default TransactionEntry;
